import { useSubmit } from "@certn/api";
import { showToast } from "@certn/cascadia";
import { Formik } from "formik";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useCanViewClientPortalPricing } from "../../../../components/permissions/Permissions";
import { useAllowedCurrencyForSubsidiary } from "../../../../hooks/useAllowedCurrencyForSubsidiary";
import { useClientAccount } from "../../../../hooks/useClientAccount";
import { Subsidiary } from "../../../../types";
import { CriminalRecordTemplateFormFields } from "../../components/CriminalRecordTemplateFormFields";
import {
  AUSTRALIAN_CRIMINAL_OPTION,
  CCRC_LEVEL,
  CRIMINAL_RECORD_VALIDATION,
  US_CRIMINAL_PREFERENCE,
} from "../../constants";
import { useAustralianOrderingFlags } from "../../hooks";
import { CriminalRecordTemplate } from "../../types";
import {
  australianOptionToCheckArguments,
  criminalRecordTemplateSchema,
  usPreferenceToCheckArguments,
} from "../../utils";
import { CriminalRecordTemplatePageLayout } from "../CriminalRecordTemplatePageLayout";
import { useCriminalRecordTemplatesContext } from "../CriminalRecordTemplatesContext";

export function CriminalRecordTemplateDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    clientPortalCriminalTemplatesEnhancements: criminalTemplatesEnhancements,
  } = useFlags();
  const { criminalRecordTemplate } = useCriminalRecordTemplatesContext();
  const [enableMaximumPrice, setEnableMaximumPrice] = useState(false);
  const australianOrderingFlags = useAustralianOrderingFlags(
    criminalTemplatesEnhancements
  );
  const { client } = useClientAccount();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const allowedCurrencies = useAllowedCurrencyForSubsidiary(
    client?.subsidiary as Subsidiary,
    true
  );
  const [selectedCurrency, setSelectedCurrency] = useState<string>(
    allowedCurrencies[0]?.id || "CAD"
  );

  const schema = criminalRecordTemplateSchema();

  const { submit } = useSubmit(
    "/api/internal/client-portal/check-arguments-templates/criminal-record/create/",
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Template successfully created"), { type: "success" });
        navigate("/settings/criminal-record-templates");
      },
    }
  );

  return (
    <Formik
      initialValues={criminalRecordTemplate}
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const maximum_price =
            enableMaximumPrice &&
            values.check_arguments?.maximum_price?.[selectedCurrency]
              ? {
                  [selectedCurrency]:
                    values.check_arguments.maximum_price[selectedCurrency],
                }
              : null;

          const payload: CriminalRecordTemplate = {
            name: values.name,
            check_arguments: {
              umbrella_minimum_address_duration_months:
                values.check_arguments
                  ?.umbrella_minimum_address_duration_months ||
                CRIMINAL_RECORD_VALIDATION.DEFAULT_DURATION_MONTHS,
              umbrella_address_history_range_months:
                (values.lookback_years ||
                  CRIMINAL_RECORD_VALIDATION.DEFAULT_LOOKBACK_YEARS) * 12,
              umbrella_apply_residency_rules_to_current_address:
                values.check_arguments
                  ?.umbrella_apply_residency_rules_to_current_address ?? true,
              canadian_criminal_preference:
                values.check_arguments?.canadian_criminal_preference ||
                CCRC_LEVEL.BASIC,
              ...(criminalTemplatesEnhancements
                ? {
                    ...australianOptionToCheckArguments(
                      values.australian_criminal_option ??
                        AUSTRALIAN_CRIMINAL_OPTION.AFP
                    ),
                    ...usPreferenceToCheckArguments(
                      values.us_criminal_preference ??
                        US_CRIMINAL_PREFERENCE.NONE,
                      values.us_criminal_checks
                    ),
                  }
                : {}),
              maximum_price,
            },
          };
          await submit(payload);
        } catch (error) {
          console.error(error);
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
        <CriminalRecordTemplatePageLayout
          onComplete={handleSubmit}
          submitting={isSubmitting}
        >
          <CriminalRecordTemplateFormFields
            isDisabled={false}
            values={values}
            setFieldValue={setFieldValue}
            allowedCurrencies={allowedCurrencies}
            selectedCurrency={selectedCurrency}
            setSelectedCurrency={setSelectedCurrency}
            enableMaximumPrice={enableMaximumPrice}
            setEnableMaximumPrice={setEnableMaximumPrice}
            australianOrderingFlags={australianOrderingFlags}
            showAustralianPreferences={criminalTemplatesEnhancements}
            showUsPreferences={criminalTemplatesEnhancements}
            isUsCatalogPricingVisible={canViewClientPortalPricing}
          />
        </CriminalRecordTemplatePageLayout>
      )}
    </Formik>
  );
}
